import { useContact, usePageModels } from '@/context';
import { DataLayerEventNames, DataLayerEvents } from '@/datalayer/App.datalayer.constants';
import { useDataLayerEvent } from '@/hooks';
import { equalUrlPathNames } from '@/utils/url';
import React from 'react';
import TagManager from 'react-gtm-module';
import { useLastLocation } from 'react-router-last-location';
import { useStorageState } from 'react-storage-hooks';
import { SEOProps } from './SEO.interface';

const removeIndexDescription = (): void => {
  const descriptionNode = document.querySelector('meta[name="description"]:not([data-rh])');
  if (!descriptionNode) return;

  descriptionNode?.remove();
};

// Using this approach instead of "useStorageState" to avoid race conditions.
const lastCustomUrl = {
  get: () => {
    const value = sessionStorage.getItem('SEO.LAST_CUSTOM_URL');
    // sessionStorage stores the value with quotes.
    if (value?.startsWith('"') && value?.endsWith('"')) return value.slice(1, -1);

    return value || '';
  },
};

export const useSEO = ({ metadata, httpStatusCode, isDocumentBackedPage }: Partial<SEOProps>) => {
  const {
    metaTitle: title,
    description,
    canonicalUrl,
    vanityUrl,
    noFollow,
    noIndex,
    searchDisplayTitle,
    searchDisplayDescription,
  } = metadata || {};
  const { contactInfo } = useContact();
  const pageModels = usePageModels();
  const { language: langCodeDefault = '' } = pageModels;
  const [isGTMLoaded, setIsGTMLoaded] = React.useState(false);
  const [delayedPageViewEvents, setDelayedPageViewEvents] = useStorageState<
    Record<string, string>[]
  >(sessionStorage, 'DATALAYER.DELAYED_PAGE_VIEW_EVENTS_OBJECTS', []);
  const pageViewDataLayerRef = React.useRef({});
  const customUrlRef = React.useRef('');

  const [, setLastCustomUrl] = useStorageState<string>(sessionStorage, 'SEO.LAST_CUSTOM_URL', '');
  const [isLoggingOutAttempt, setIsLoggingOutAttempt] = useStorageState<boolean>(
    sessionStorage,
    'DATALAYER.IS_LOGGING_OUT_ATTEMPT',
    false,
  );
  const [isLoggingInAttempt, setIsLoggingInAttempt] = useStorageState<boolean>(
    sessionStorage,
    'DATALAYER.IS_LOGGING_IN_ATTEMPT',
    false,
  );
  const lastLocation = useLastLocation();

  const robots = React.useMemo(
    () => [noIndex ? 'noindex' : 'index', noFollow ? 'nofollow' : 'follow'],
    [noFollow, noIndex],
  );
  const currentUrl = `${document.location.origin}${document.location.pathname}`;
  const customUrl = React.useMemo(
    () => (canonicalUrl || vanityUrl || currentUrl || '').replace(/\/$/, ''),
    [canonicalUrl, vanityUrl, currentUrl],
  );
  const metaTitle = React.useMemo(
    () => (isDocumentBackedPage ? title : title || searchDisplayTitle),
    [title, searchDisplayTitle, isDocumentBackedPage],
  );

  const metaDescription = React.useMemo(
    () => (isDocumentBackedPage ? description : description || searchDisplayDescription),
    [description, searchDisplayDescription, isDocumentBackedPage],
  );

  const hasHttpStatusCode = React.useMemo(
    () => !!httpStatusCode && !/^200$/.test(httpStatusCode),
    [httpStatusCode],
  );

  React.useEffect(() => {
    customUrlRef.current = customUrl;
  }, [customUrl]);

  React.useEffect(() => removeIndexDescription(), []);

  React.useLayoutEffect(() => {
    const delayedPageViewEventsCatcher = (event: Event) => {
      setDelayedPageViewEvents((prev) => [
        ...prev,
        (event as CustomEvent).detail.target as Record<string, string>,
      ]);
    };

    window.addEventListener(DataLayerEvents.DELAYED_PAGE_VIEW_EVENTS, delayedPageViewEventsCatcher);
    return () =>
      window.removeEventListener(
        DataLayerEvents.DELAYED_PAGE_VIEW_EVENTS,
        delayedPageViewEventsCatcher,
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * This effect is used to set the dataLayer for the page view event.
   */
  React.useEffect(() => {
    const pagePath = window.document.location.href.replace(window.location.origin, '');

    pageViewDataLayerRef.current = {
      event: 'gaCustomEventAsync_PageView',
      event_name: 'page_view',
      ...(!!contactInfo?.contactId && { user_id: contactInfo.contactId }),
      ...(!!contactInfo?.accountType && { account_type: contactInfo.accountType }),
      ...(!!contactInfo?.portalRegistrationStatus && {
        registration_status: contactInfo.portalRegistrationStatus,
      }),
      login_status: contactInfo?.contactId ? 'logged_in' : 'anonymous',
      language: langCodeDefault,
      page_path: pagePath,
      ...(!!metaTitle && { page_title: metaTitle }),
      content_group: pageModels?.contentGroup || undefined,
      content_group2: pageModels?.contentGroup2 || undefined,
      content_group3: pageModels?.contentGroup3 || undefined,
      content_group4: pageModels?.contentGroup4 || undefined,
      ...(!!contactInfo?.betaParticipantStatus && {
        gen_beta_participant_status: Number(contactInfo?.betaParticipantStatus).toString(),
      }),
      ...(!!contactInfo?.portalPrivacyConsent && {
        portal_tracking_status: contactInfo?.portalPrivacyConsent?.toString(),
      }),
      ...(!!lastLocation?.pathname && {
        async_page_referrer: `${lastLocation?.pathname}${lastLocation?.search}`,
      }),
    };
  }, [
    contactInfo?.accountType,
    contactInfo?.betaParticipantStatus,
    contactInfo?.contactId,
    contactInfo?.portalRegistrationStatus,
    contactInfo?.portalPrivacyConsent,
    langCodeDefault,
    metaTitle,
    pageModels?.contentGroup,
    pageModels?.contentGroup2,
    pageModels?.contentGroup3,
    pageModels?.contentGroup4,
    lastLocation?.pathname,
    lastLocation?.search,
  ]);

  /**
   * This effect is used to update the isGTMLoaded state after the GTM_EVENT_LOAD event is fired.
   */
  useDataLayerEvent(
    DataLayerEventNames.GTM_EVENT_LOAD,
    () => {
      setTimeout(() => setIsGTMLoaded(true), 200);
    },
    {
      fireOnMount: true,
      once: true,
    },
  );

  /**
   * This effect is used to push the dataLayer to dataLayer
   * and to prevent the dataLayer from being pushed on the initial render.
   */
  React.useEffect(() => {
    /**
     * This effect is used to push the dataLayer to dataLayer
     */
    if (!isGTMLoaded) return;

    /**
     * This effect is used to prevent the dataLayer from being pushed on the initial render.
     */
    const lastCustomUrlValue = lastCustomUrl.get();
    if (!lastCustomUrlValue) {
      setLastCustomUrl(customUrlRef.current);
      return;
    }

    /**
     * This effect is used to prevent the dataLayer from being pushed if the user is logging out.
     */
    if (isLoggingOutAttempt) {
      setIsLoggingOutAttempt(false);
    }

    /**
     * This effect is used to prevent the dataLayer from being pushed if the user is logging in.
     */
    if (isLoggingInAttempt) {
      setIsLoggingInAttempt(false);
    }

    /**
     * This effect is used to prevent the dataLayer from being pushed if the customUrl has not changed.
     */
    if (equalUrlPathNames(lastCustomUrlValue, customUrlRef.current)) return;

    /**
     * Push event to dataLayer and update lastCustomUrl.
     */
    TagManager.dataLayer({ dataLayer: pageViewDataLayerRef.current });
    setLastCustomUrl(customUrlRef.current);

    if (delayedPageViewEvents?.length) {
      delayedPageViewEvents.forEach((delayedPageViewEvent) => {
        TagManager.dataLayer({ dataLayer: delayedPageViewEvent });
      });
      setDelayedPageViewEvents([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadata, isGTMLoaded]);

  return { robots, customUrl, metaTitle, metaDescription, hasHttpStatusCode };
};
